import { getIdToken as getSsoIdToken, getStoredTokens } from '../cognito-sso/oauth.js';

function recallAfterOffline(XMLHttpRequest, fn) {
  if (XMLHttpRequest.readyState === 0 || XMLHttpRequest.readyState === 4) {
    // Network error (i.e. connection refused, access denied due to CORS, etc.)
    window.addEventListener(
      'online',
      () => {
        fn();
      },
      { once: true }
    );
  }
}

export async function sendFileToS3(
  file,
  { s3key, s3bucketUrl, s3fields, defaultImgPath = undefined, defaultFileType = 'image/jpeg' },
  { retryIfOffline = false, rejectOnError = false } = {}
) {
  return new Promise(function exec(resolve, reject) {
    const xhr = new XMLHttpRequest();

    xhr.addEventListener(
      'load',
      () => {
        if (rejectOnError && (xhr.status < 200 || xhr.status >= 300)) {
          reject(new Error(`S3 upload failed with status ${xhr.status}.`));
          return;
        }
        resolve(finalPath());
      },
      false
    );

    xhr.addEventListener(
      'error',
      (err) => {
        if (retryIfOffline) {
          recallAfterOffline(err.currentTarget, () => exec(resolve, reject));
        } else if (rejectOnError) {
          reject(new Error('S3 upload failed because of a network error.'));
        }
      },
      false
    );

    xhr.addEventListener(
      'abort',
      () => {
        if (rejectOnError) reject(new Error('S3 upload was aborted.'));
      },
      false
    );

    xhr.open('POST', s3bucketUrl, true); // MUST BE LAST LINE BEFORE YOU SEND

    xhr.send(prepareFormData());
  });

  function prepareFormData() {
    const fd = new FormData();

    fd.append('key', s3key);
    fd.append('acl', 'public-read');
    fd.append('success_action_status', '201');
    fd.append('Content-Type', getFileType());
    fd.append('X-Amz-Credential', s3fields['X-Amz-Credential']);
    fd.append('X-Amz-Algorithm', s3fields['X-Amz-Algorithm']);
    fd.append('X-Amz-Date', s3fields['X-Amz-Date']);
    fd.append('X-Amz-Security-Token', s3fields['X-Amz-Security-Token']);
    fd.append('Policy', s3fields.Policy);
    fd.append('X-Amz-Signature', s3fields['X-Amz-Signature']);
    fd.append('file', file);
    return fd;
  }

  function finalPath() {
    if (s3fields.bucket) {
      return `https://${s3fields.bucket}/${s3key}`;
    }
    return `${defaultImgPath}/${s3key}`;
  }

  function getFileType() {
    return file.type && file.type !== '' ? file.type : defaultFileType;
  }
}

export const initRequest = (serverUrl) => ({
  post: async (path, body = {}, { addAuth = false, retryIfOffline = false, extraHeaders = null } = {}) => {
    let auth = null;
    if (addAuth) {
      auth = await getAuthToken();
    }
    return new Promise(function exec(resolve, reject) {
      const xhr = new XMLHttpRequest();

      xhr.responseType = 'json';

      xhr.addEventListener(
        'load',
        (event) => {
          resolve(event.currentTarget.response);
        },
        false
      );

      xhr.addEventListener(
        'error',
        (err) => {
          if (retryIfOffline) {
            recallAfterOffline(err.currentTarget, () => exec(resolve));
          } else {
            reject(err.currentTarget);
          }
        },
        false
      );

      xhr.open('POST', `${serverUrl}${path}`, true); // MUST BE LAST LINE BEFORE YOU SEND

      if (auth) {
        xhr.setRequestHeader('Authorization', auth);
      }

      if (extraHeaders) {
        Object.entries(extraHeaders).forEach(([name, value]) => {
          if (value != null) {
            xhr.setRequestHeader(name, value);
          }
        });
      }

      xhr.send(JSON.stringify(body));
    });
  },
});

// Shared instance for the main backend — new API modules should import this
// instead of re-initializing per module.
export const backendApi = initRequest(process.env.BACKEND_API);

async function getAuthToken() {
  const { idToken } = getStoredTokens();
  if (idToken) {
    return getSsoIdToken();
  }
  // SDK fallback is only for non-SSO projects (webapp) that still load
  // the AmazonCognitoIdentity script. SSO projects (occo-portal, photo-album,
  // occo-admin) don't load it — with no stored SSO token the user is simply
  // unauthenticated, so reject cleanly instead of throwing "SDK not included".
  if (typeof AmazonCognitoIdentity !== 'undefined') {
    return getAuthTokenFromSdk();
  }
  throw new Error('unauthorized_user');
}

function getAuthTokenFromSdk() {
  return new Promise((resolve, reject) => {
    const cognitoUser = getCurrentUser();
    if (cognitoUser == null) {
      reject(new Error('unauthorized_user'));
      return;
    }
    cognitoUser.getSession((err, session) => {
      if (err || !session.isValid()) {
        const error = { type: 'unauthorized_user' };
        if (err) {
          error.innerError = err;
        }
        reject(error);
      } else {
        resolve(session.getIdToken().getJwtToken());
      }
    });

    function getCurrentUser() {
      if (!process.env.POOL_DATA_USER_ID) {
        throw new Error('POOL_DATA_USER_ID must be defined');
      }
      if (!process.env.POOL_DATA_CLIENT_ID) {
        throw new Error('POOL_DATA_CLIENT_ID must be defined');
      }
      if (!AmazonCognitoIdentity) {
        throw new Error('AmazonCognitoIdentity should be included');
      }
      const userPool = new AmazonCognitoIdentity.CognitoUserPool({
        UserPoolId: process.env.POOL_DATA_USER_ID,
        ClientId: process.env.POOL_DATA_CLIENT_ID,
      });
      return userPool.getCurrentUser();
    }
  });
}

/**
 * Recursively transforms ISO-8601 UTC date strings (e.g. 2025-12-01T14:05:51.000Z)
 * into Date objects. If the input is a date string, a Date is returned.
 * If the input is an object or array, a copy is returned where properties named
 * \`dateString\` (and top-level strings that match the ISO pattern) are converted
 * to \`Date\` objects. The original input is not mutated.
 *
 * @param {*} input - Any value: string, object, array, or primitive.
 *                    Strings matching the ISO pattern and/or properties named
 *                    \`dateString\` will be converted to \`Date\`.
 * @returns {*} The transformed value: a \`Date\` for a string input, or a structure
 *              (object/array) with replaced Date values.
 *
 * @example
 * // String -> Date
 * // transformStringDateToDate('2025-12-01T14:05:51.000Z') // => Date
 *
 * @example
 * // Object with dateString property
 * // transformStringDateToDate({ dateString: '2025-12-01T14:05:51.000Z' })
 * // => { dateString: Date }
 */
export function transformStringDateToDate(input) {
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
  const seen = new WeakMap();

  function transform(value) {
    // string -> Date if matches ISO pattern
    if (typeof value === 'string') {
      return isoRegex.test(value) ? new Date(value) : value;
    }

    // keep Date instances as-is
    if (value instanceof Date) return value;

    // primitives and null
    if (value === null || typeof value !== 'object') return value;

    // handle circular references
    if (seen.has(value)) return seen.get(value);

    // arrays
    if (Array.isArray(value)) {
      const outArr = value.map((el) => transform(el));
      seen.set(value, outArr);
      return outArr;
    }

    // plain or custom objects: preserve prototype
    const proto = Object.getPrototypeOf(value);
    const outObj = Object.create(proto);
    seen.set(value, outObj);
    for (const [k, v] of Object.entries(value)) {
      outObj[k] = transform(v);
    }
    return outObj;
  }

  return transform(input);
}
